import React from 'react';
import { X } from 'lucide-react';
import { Button, RobbieMark } from '../../ui';
import JourneyPreview from '../empty/JourneyPreview';

/**
 * The expanded welcome for someone who has created nothing here yet, in a
 * workspace that is already busy. It sits ABOVE the dashboard and never
 * replaces it — the org's tiles and table stay visible underneath, because
 * this person may have come to read them, not to source anything.
 *
 * So the copy DESCRIBES rather than assumes: it explains how a sourcing
 * project runs and offers to start one, instead of the empty-workspace
 * "let's source your first part", which would be wrong for a finance reader
 * who will never open an RFQ.
 */
export interface WelcomePanelProps {
  /** Greeting name — the sign-in name's first word. */
  firstName: string;
  /** Org-wide active RFQs, so the panel can say whose numbers those are. */
  activeTotal: number;
  onStart: () => void;
  onDismiss: () => void;
  dismissing?: boolean;
  /** The dismissal did not reach the server — say so, don't pretend. */
  dismissFailed?: boolean;
}

export const WelcomePanel: React.FC<WelcomePanelProps> = ({
  firstName,
  activeTotal,
  onStart,
  onDismiss,
  dismissing = false,
  dismissFailed = false,
}) => (
  <section className="rounded-panel border border-robbie-border bg-ai-panel px-[26px] py-s6">
    <div className="flex flex-wrap items-start gap-s6">
      <RobbieMark size="lg" state="waving" aria-label="Robbie" />
      <div className="min-w-[240px] flex-1">
        <h2 className="m-0 mb-s2 text-[18px] font-bold tracking-[-0.01em] text-ink">
          Hi {firstName} — new here? Here's how I work.
        </h2>
        <p className="m-0 text-[13.5px] leading-[1.6] text-ink-secondary">
          I take a sourcing project end to end: I read the Bill of Materials, find suppliers who can
          genuinely make the parts, run the outreach, and bring back quotes ready to award. You approve
          every step.
        </p>
        {activeTotal > 0 && (
          <p className="m-0 mt-s3 text-meta text-ink-muted">
            Nothing below is yours yet — those are the workspace's {activeTotal} active{' '}
            {activeTotal === 1 ? 'RFQ' : 'RFQs'}, from everyone on the team.
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<X />}
        onClick={onDismiss}
        disabled={dismissing}
        className="shrink-0"
      >
        {dismissing ? 'Saving…' : 'Got it'}
      </Button>
    </div>

    <div className="mt-s6">
      <JourneyPreview heading="How a sourcing project runs" />
    </div>

    <div className="mt-s5 flex flex-wrap items-center gap-s4">
      <Button size="lg" onClick={onStart}>Start your first RFQ</Button>
      <span className="text-meta text-ink-muted">
        Or just read — everything the workspace is running is below.
      </span>
    </div>

    {dismissFailed && (
      <p className="mt-s4 text-meta text-ink-muted" role="alert">
        I couldn't save that just now, so this will still be here next time.
      </p>
    )}
  </section>
);

export default WelcomePanel;
